'use client';

import { ConvexBetterAuthProvider } from '@convex-dev/better-auth/react';
import { ConvexQueryClient } from '@convex-dev/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConvexReactClient, useConvexAuth, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { ConfirmedConvexIdentityContext } from './protected-workspace-boundary';
import { RenderIdentityContext } from './render-identity';
import { ConvexQueryCacheProvider } from 'convex-helpers/react/cache';
import { ReactNode, useEffect, useState } from 'react';
import { authClient } from '../lib/better-auth-client-actions';
import { createConvexQueryAuthLifecycle } from '../lib/convex-query-auth-lifecycle';
import { buildConvexClientOptions } from '../lib/convex-client-options';
import { createDeploySkewAwareConvexLogger } from '../lib/deploy-skew-convex-logger';

function createRuntime(expectAuth: boolean) {
  const options = buildConvexClientOptions({
    verbose: process.env.NEXT_PUBLIC_CONVEX_VERBOSE,
    expectAuth,
  });
  const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL!, {
    ...options,
    // Notices InvalidCursor resets so a follow-on crash shows the update screen.
    logger: createDeploySkewAwareConvexLogger({
      verbose: options.verbose ?? false,
    }),
  });
  const convexQueryClient = new ConvexQueryClient(convex);
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        queryKeyHashFn: convexQueryClient.hashFn(),
        queryFn: convexQueryClient.queryFn(),
      },
    },
  });
  if (typeof window !== 'undefined') convexQueryClient.connect(queryClient);
  return {
    convex,
    queryClient,
    updateConvexQueryIdentity: createConvexQueryAuthLifecycle(queryClient),
  };
}
type ConvexRuntime = ReturnType<typeof createRuntime>;
let browserRuntime: ConvexRuntime | undefined;

function getRuntime(expectAuth: boolean): ConvexRuntime {
  // Request-scoped on the server; one connection/cache per browser document.
  if (typeof window === 'undefined') return createRuntime(expectAuth);
  return (browserRuntime ??= createRuntime(expectAuth));
}

function ConvexQueryAuthLifecycle({
  children,
  initialIdentity,
  runtime,
}: {
  children: ReactNode;
  initialIdentity: string | null;
  runtime: ConvexRuntime;
}) {
  const { queryClient, updateConvexQueryIdentity } = runtime;
  // Server-rendered workspace data was fetched for this identity; keep it
  // through the startup confirmation instead of purging it.
  useState(() => {
    if (initialIdentity)
      updateConvexQueryIdentity.seedServerIdentity(initialIdentity);
    return null;
  });
  const { data: session, isPending } = authClient.useSession();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const userId = session?.user.id ?? null;
  const viewer = useQuery(
    api.users.queries.viewer,
    !isPending && !isLoading && isAuthenticated && userId ? {} : 'skip'
  );
  const confirmedIdentity =
    !isPending && !isLoading && isAuthenticated && viewer?._id === userId
      ? userId
      : null;

  const [admission, setAdmission] = useState({
    identity: confirmedIdentity,
    ready: false,
  });
  if (admission.identity !== confirmedIdentity) {
    setAdmission({ identity: confirmedIdentity, ready: false });
  }

  useEffect(() => {
    let current = true;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const attempt = () => {
      attempts += 1;
      void updateConvexQueryIdentity(confirmedIdentity)
        .then(() => {
          if (current)
            setAdmission({ identity: confirmedIdentity, ready: true });
        })
        .catch(() => {
          if (!current) return;
          setAdmission({ identity: confirmedIdentity, ready: false });
          if (attempts < 3)
            retryTimer = setTimeout(attempt, 250 * 2 ** (attempts - 1));
        });
    };
    attempt();
    return () => {
      current = false;
      clearTimeout(retryTimer);
    };
  }, [confirmedIdentity, updateConvexQueryIdentity]);

  // While the session loads, render as the request token's identity so a
  // matching session doesn't remount the whole app when it resolves.
  const renderIdentity = (isPending ? initialIdentity : userId) ?? null;
  return (
    <ConfirmedConvexIdentityContext.Provider
      value={
        admission.identity === confirmedIdentity && admission.ready
          ? confirmedIdentity
          : null
      }
    >
      <RenderIdentityContext.Provider value={renderIdentity}>
        <ConvexQueryCacheProvider key={renderIdentity ?? 'anonymous'}>
          <QueryClientProvider client={queryClient}>
            {children}
          </QueryClientProvider>
        </ConvexQueryCacheProvider>
      </RenderIdentityContext.Provider>
    </ConfirmedConvexIdentityContext.Provider>
  );
}

export function ConvexClientProvider({
  children,
  initialToken,
  initialIdentity = null,
}: {
  children: ReactNode;
  initialToken?: string | null;
  /** Subject of `initialToken`; a cache-keying hint, never authorization. */
  initialIdentity?: string | null;
}) {
  const [runtime] = useState(() => getRuntime(Boolean(initialToken)));
  return (
    <ConvexBetterAuthProvider
      client={runtime.convex}
      authClient={authClient}
      initialToken={initialToken ?? undefined}
    >
      <ConvexQueryAuthLifecycle
        initialIdentity={initialIdentity}
        runtime={runtime}
      >
        {children}
      </ConvexQueryAuthLifecycle>
    </ConvexBetterAuthProvider>
  );
}
